import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { colors } from "style";
import Modal, { MODAL_SIZE } from "components/Modal";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";

/**
 * VideoThumbnail
 *
 * @param {String}    src
 * @param {String}    thumbnail
 * @param {Object}    previewProps
 * @param {String}    thumbnailAlt
 * @param {Function}  onSecondaryButtonClick
 * @param {Boolean}   showPlayButton
 * @param {Boolean}   showSecondaryButton
 */
const VideoThumbnail = ({
  src,
  thumbnail,
  previewProps = {},
  thumbnailAlt = "Video thumbnail",
  onSecondaryButtonClick = () => {},
  showPlayButton = true,
  showSecondaryButton = false,
  ...props
}) => {
  const [open, setOpen] = useState(false);

  if (!src)
    return (
      <Placeholder>
        <FormattedMessage id="Video.VideoThumbnail.NotAvailable" />
      </Placeholder>
    );

  return (
    <>
      <Wrapper onClick={() => setOpen(true)} {...props}>
        {thumbnail ? <PreviewImage src={thumbnail} alt={thumbnailAlt} /> : <ThumbnailPlaceholder />}

        <Overlay className="overlay">
          <ActionIconGroup>
            {showPlayButton && (
              <PlayButton>
                <PlayIcon />
              </PlayButton>
            )}
            {showSecondaryButton && (
              <ActionIconButton
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onSecondaryButtonClick();
                }}
              >
                <Icon type={ICON_TYPE.edit} size={ICON_SIZE.medium} color={colors.grayAnatomyBase} />
              </ActionIconButton>
            )}
          </ActionIconGroup>
        </Overlay>
      </Wrapper>

      <Modal
        show={open}
        onClose={() => setOpen(false)}
        size={MODAL_SIZE.small}
        showSave={false}
        secondaryButtonMessageId="Global.Close"
        clickOutsideToClose
        {...previewProps}
      >
        <ModalVideo src={src} controls autoPlay onEnded={() => setTimeout(() => setOpen(false), 700)} />
      </Modal>
    </>
  );
};

const Wrapper = styled.div`
  aspect-ratio: 16 / 9;
  width: 100%;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background: ${colors.black};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);

  &:hover .overlay {
    background: rgba(0, 0, 0, 0.4);
  }
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  transition: background 0.2s ease;
`;

const PlayButton = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlayIcon = styled.div`
  width: 0;
  height: 0;
  margin-left: 1px;
  border-left: 7px solid ${colors.black};
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
`;

const ActionIconGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const ActionIconButton = styled.button`
  width: 3.2rem;
  height: 3.2rem;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 1);
  }
`;

const ModalVideo = styled.video`
  width: 100%;
  border-radius: 8px;
`;

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, ${colors.grayAnatomyBase2}, ${colors.grayAnatomyBase});
  filter: blur(0.5px);
`;

const Placeholder = styled.div`
  width: 88px;
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.grayAnatomyBase2};
  font-size: 12px;
`;

VideoThumbnail.propTypes = {
  src: PropTypes.string.isRequired,
  thumbnail: PropTypes.string,
  previewProps: PropTypes.object,
  thumbnailAlt: PropTypes.string,
  onSecondaryButtonClick: PropTypes.func,
  showPlayButton: PropTypes.bool,
  showSecondaryButton: PropTypes.bool,
};

export default VideoThumbnail;
